import { useEffect, type ReactElement } from 'react';

import { CONSENT_GRANTED_EVENT, CONSENT_STORAGE_KEY } from '~/components/privacy/CookieConsent';

/** Marker we set on `window` once the GTM snippet is in the DOM. */
declare global {
  interface Window {
    __gtmInjected?: boolean;
    dataLayer?: unknown[];
  }
}

const NOSCRIPT_ID = 'arpa-gtm-noscript';

function consentGranted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === 'granted';
  } catch {
    return false;
  }
}

/**
 * Inline GTM bootstrap. Mirrors the snippet recommended by Google but is
 * built dynamically rather than written into <head> at server-render time
 * so it never fires unless both the env var and consent gates pass.
 *
 * `dataLayer` is initialised upstream by the Google Consent Mode v2 default
 * script injected inline in <head> (see `~/lib/consentMode.ts`); we do not
 * re-initialise it here.
 */
function injectGtm(gtmId: string): void {
  if (typeof window === 'undefined') return;
  if (window.__gtmInjected === true) return;

  const script = document.createElement('script');
  script.text = [
    "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':",
    "new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],",
    "j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=",
    "'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);",
    `})(window,document,'script','dataLayer','${gtmId}');`,
  ].join('');
  document.head.appendChild(script);

  if (document.getElementById(NOSCRIPT_ID) === null) {
    const noscript = document.createElement('noscript');
    noscript.id = NOSCRIPT_ID;
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  window.__gtmInjected = true;
}

/**
 * Mounts a side-effect component that conditionally loads Google Tag Manager.
 *
 * Behaviour:
 *   - Reads `import.meta.env.VITE_GTM_ID`. If absent or empty, does nothing.
 *   - On mount, checks `localStorage.getItem('arpa:consent') === 'granted'`
 *     and injects the loader immediately if the user already consented.
 *   - Otherwise subscribes to the `arpa:consent-granted` event so a later
 *     "Accept" click on the banner triggers the load without a reload.
 *   - Idempotent: a `window.__gtmInjected` flag guards against double-mount
 *     (StrictMode runs effects twice in dev).
 */
export function GtmLoader(): ReactElement | null {
  useEffect(() => {
    const gtmId = import.meta.env.VITE_GTM_ID;
    if (typeof gtmId !== 'string' || gtmId.length === 0) return;

    if (consentGranted()) {
      injectGtm(gtmId);
      return;
    }

    const handler = (): void => {
      if (consentGranted()) injectGtm(gtmId);
    };
    window.addEventListener(CONSENT_GRANTED_EVENT, handler);
    return () => {
      window.removeEventListener(CONSENT_GRANTED_EVENT, handler);
    };
  }, []);

  return null;
}

export default GtmLoader;
